import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";

import {
  Dialog,
  ErrorMessage,
  ErrorButton,
  PrimaryButton,
  SingleSelect,
  Text,
  Textfield,
} from "widgets";
import {
  useFetchLoanProviderDetails,
  usePunchPayment,
  useCreateLoanIntent,
} from "store/backend/assets";
import PaymentModes from "components/PaymentModes/PaymentModes";

import Loader from "Shared/Loader/Loader";
import { isEmpty } from "lodash";
import { reduceToFixedDecimals } from "utils/amountCalculation";
import {
  PAYMENT_PLANS,
  LOAN_PAYMENT_METHOD,
  PAYMENT_STATUSES,
  SUCCESSFUL_PAYMENT_STATUSES,
  LOAN_STATUSES,
} from "constants/config";
import {
  CREATE_LOAN_INTENT_ERROR_MESSAGES,
  PUNCH_PAYMENT_V2_ERROR_MESSAGES,
  getApiErrorMessage,
} from "constants/apiErrorMessages";
import Detail from "widgets/DetailDisplay";

import styles from "./LoanPayment.module.scss";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

const getLoanProvider = (loanProviderDetails) => {
  return loanProviderDetails.map((loanProvider) => ({
    id: loanProvider.id,
    value: loanProvider.id,
    label: loanProvider.provider,
  }));
};

const getTenureOptions = (tenureOptions) => {
  const sorted = Array.isArray(tenureOptions)
    ? [...tenureOptions].sort(
        (tenureOptionA, tenureOptionB) =>
          (tenureOptionA.tenure_in_months ?? 0) -
          (tenureOptionB.tenure_in_months ?? 0),
      )
    : [];
  return sorted.map((emiDivisionOption) => ({
    ...emiDivisionOption,
    label: emiDivisionOption.tenure_in_months,
    value: emiDivisionOption,
  }));
};

const getSelectedProviderLabel = (providerId, loanProviderDetails) => {
  if (!providerId || !Array.isArray(loanProviderDetails)) return "—";
  const matchedProvider = loanProviderDetails.find(
    (provider) => provider.id === providerId,
  );
  return matchedProvider ? matchedProvider.provider : "—";
};

const handleTenureChange = (
  event,
  setSelectedTenure,
  setLoanApplicationNumber,
  setLoanAmountErrorLogic,
  setLoanIntentData,
  setIsLoanIntentCreated,
) => {
  setSelectedTenure(event.target.value);
  setLoanApplicationNumber("");
  setLoanAmountErrorLogic("");
  setLoanIntentData(null);
  setIsLoanIntentCreated(false);
};

const handleProviderChange = (
  event,
  setSelectedProviderId,
  setTenureOptions,
  setSelectedTenure,
  loanProviderDetails,
  setLoanApplicationNumber,
  setLoanAmountErrorLogic,
  setLoanIntentData,
  setIsLoanIntentCreated,
) => {
  const providerId = event.target.value;
  setSelectedProviderId(providerId);
  const selectedProvider = loanProviderDetails.find(
    (loanProvider) => loanProvider.id === providerId,
  );
  if (selectedProvider) {
    setTenureOptions(selectedProvider.emi_divisions);
    setSelectedTenure(null);
    setLoanApplicationNumber("");
    setLoanAmountErrorLogic("");
    setLoanIntentData(null);
    setIsLoanIntentCreated(false);
  }
};

const getEmiDivisionDetailId = (selectedTenure) => {
  if (selectedTenure == null) return null;
  return selectedTenure.id;
};

const getLoanIntentDisplayFromOrder = (loanIntent, loanProviderDetails) => {
  if (
    !loanIntent?.loan_emi_division_detail_id ||
    !Array.isArray(loanProviderDetails)
  )
    return {
      providerName: "—",
      tenureMonths: "—",
      loanApplicationAmount: loanIntent?.loan_application_amount ?? "—",
    };
  const emiDivisionId = loanIntent.loan_emi_division_detail_id;
  for (const provider of loanProviderDetails) {
    const divisions = Array.isArray(provider.emi_divisions)
      ? provider.emi_divisions
      : [];
    const division = divisions.find((emiDivision) =>
      matchEmiDivisionId(emiDivision, emiDivisionId),
    );
    if (division) {
      return {
        providerName: provider.provider ?? "—",
        tenureMonths: division.tenure_in_months ?? "—",
        loanApplicationAmount: loanIntent.loan_application_amount ?? "—",
      };
    }
  }
  return {
    providerName: "—",
    tenureMonths: "—",
    loanApplicationAmount: loanIntent?.loan_application_amount ?? "—",
  };
};

const getTenureFromOrderLoanIntent = (orderLoanIntent, loanProviderDetails) => {
  if (
    !orderLoanIntent?.loan_emi_division_detail_id ||
    !Array.isArray(loanProviderDetails)
  )
    return null;
  const emiDivisionId = orderLoanIntent.loan_emi_division_detail_id;
  for (const provider of loanProviderDetails) {
    const divisions = Array.isArray(provider.emi_divisions)
      ? provider.emi_divisions
      : [];
    const division = divisions.find((emiDivision) =>
      matchEmiDivisionId(emiDivision, emiDivisionId),
    );
    if (division) return division;
  }
  return null;
};

const matchEmiDivisionId = (division, emiDivisionId) => {
  if (emiDivisionId == null) return false;
  const normalize = (value) =>
    value != null ? String(value).trim().toLowerCase() : "";
  const target = normalize(emiDivisionId);
  if (!target) return false;
  const tryMatch = (value) =>
    value != null &&
    (value === emiDivisionId ||
      normalize(value) === target);
  return (
    tryMatch(division.emi_division_detail_id) || tryMatch(division.id)
  );
};

const getProviderAndTenureFromOrderLoanIntent = (
  orderLoanIntent,
  loanProviderDetails,
) => {
  if (
    !orderLoanIntent?.loan_emi_division_detail_id ||
    !Array.isArray(loanProviderDetails)
  )
    return { providerId: null, tenureOption: null };
  const emiDivisionId = orderLoanIntent.loan_emi_division_detail_id;
  for (const provider of loanProviderDetails) {
    const divisions = Array.isArray(provider.emi_divisions)
      ? provider.emi_divisions
      : [];
    const division = divisions.find((emiDivision) =>
      matchEmiDivisionId(emiDivision, emiDivisionId),
    );
    if (division)
      return { providerId: provider.id, tenureOption: division };
  }
  return { providerId: null, tenureOption: null };
};

const handleCreateLoanIntent = (
  selectedOrderId,
  intendedLoanAmount,
  selectedTenure,
  createLoanIntent,
  setLoanIntentData,
  openSnackbar,
) => {
  const emiDivisionDetailId = getEmiDivisionDetailId(selectedTenure);
  if (!emiDivisionDetailId) {
    openSnackbar(
      "Loan EMI division is required. Please select a tenure.",
      "error",
    );
    return;
  }
  const payload = {
    order_id: selectedOrderId,
    intended_loan_application_amount: intendedLoanAmount,
    loan_emi_division_detail_id: emiDivisionDetailId,
  };
  createLoanIntent(payload, {
    onSuccess: (createLoanIntentResponse) => {
      console.log("Create Loan Intent Response:", createLoanIntentResponse);
      setLoanIntentData(createLoanIntentResponse);
      openSnackbar("Loan intent created successfully", "success");
    },
    onError: (createLoanIntentError) => {
      const message = getApiErrorMessage(
        createLoanIntentError,
        CREATE_LOAN_INTENT_ERROR_MESSAGES,
      );
      openSnackbar(message, "error");
    },
  });
};

const getPunchLoanAmount = (loanApplicationAmount, downPayment) => {
  const app = Number(loanApplicationAmount) || 0;
  const dp = Number(downPayment) || 0;
  return Math.max(0, app - dp);
};

/**
 * Resolve down payment amount for punch (from-order flow).
 * Prefer: 1) order loan_intent.down_payment (order info API)
 *         2) tenure's down_payment_percentage × loan_application_amount
 *         3) intendedDownPayment (derived from remaining_down_payment_amount)
 */
const getDownPaymentForPunch = (
  orderLoanIntent,
  intendedDownPayment,
  resolvedTenure,
  loanApplicationAmount,
) => {
  if (
    orderLoanIntent?.down_payment != null &&
    orderLoanIntent?.down_payment !== ""
  )
    return Number(orderLoanIntent.down_payment) || 0;
  const appAmount = Number(loanApplicationAmount) || 0;
  const pct = resolvedTenure?.down_payment_percentage;
  if (pct != null && appAmount > 0)
    return reduceToFixedDecimals(appAmount * (Number(pct) / 100));
  return Number(intendedDownPayment) || 0;
};

const handlePunchPayment = (
  selectedOrderId,
  loanAmount,
  loanApplicationNumber,
  loanIntentData,
  selectedTenure,
  switchFlow,
  punchPayment,
  setDialogContent,
  setDialogOpen,
  openSnackbar,
) => {
  const emiDivisionDetailId = getEmiDivisionDetailId(selectedTenure);
  const amountToPunch =
    loanAmount != null && loanAmount > 0
      ? loanAmount
      : getPunchLoanAmount(
          loanIntentData?.loan_application_amount ?? loanIntentData?.loan_amount,
          loanIntentData?.down_payment ?? 0,
        );
  const payload = {
    order_id: selectedOrderId,
    amount: amountToPunch,
    switch_flow: switchFlow,
    payment_mode_details: {
      loan_id: loanApplicationNumber.trim(),
      loan_emi_division_detail_id: emiDivisionDetailId,
      payment_mode: "LOAN",
    },
  };
  punchPayment(payload, {
    onSuccess: (punchPaymentResponse) => {
      const data = punchPaymentResponse?.data ?? punchPaymentResponse;
      const paymentId =
        data?.payment_id ?? punchPaymentResponse?.payment_id ?? "";
      const loanId =
        data?.payment_mode_details?.loan_id ??
        punchPaymentResponse?.payment_mode_details?.loan_id ??
        loanApplicationNumber;
      const successContent = (
        <>
          <div style={{ marginBottom: "0.5rem" }}>
            Loan Successfully punched!
          </div>
          <div>
            <strong>Loan Application Number:</strong>{" "}
            {loanApplicationNumber.trim() || "—"}
          </div>
        </>
      );
      setDialogContent(successContent);
      setDialogOpen(true);
    },
    onError: (punchPaymentError) => {
      const message = getApiErrorMessage(
        punchPaymentError,
        PUNCH_PAYMENT_V2_ERROR_MESSAGES,
      );
      openSnackbar(message, "error");
    },
  });
};
const isFormValidForLoanIntent = (
  selectedProviderId,
  selectedTenure,
  intendedLoanAmount,
) => {
  return (
    selectedProviderId !== null &&
    selectedTenure != null &&
    getEmiDivisionDetailId(selectedTenure) != null &&
    intendedLoanAmount > 0
  );
};

const isFormValidForPunchLoan = (loanApplicationNumber) => {
  return loanApplicationNumber.trim() !== "";
};

const handleDialogClose = (setDialogOpen) => setDialogOpen(false);

const LoanPayment = (props) => {
  const {
    totalAmount,
    selectedOrderId,
    selectedOrderVerticalId,
    salesPersonId,
    paymentDetails = [],
    orderAmountPaid = 0,
    loanIntent,
    fromSwitchToLoan = false,
  } = props;
  const { openSnackbar } = useSnackbarContext();
  const { isPending: isLoanProviderDetailLoading, data: loanProviderDetails } =
    useFetchLoanProviderDetails(selectedOrderVerticalId);
  const { mutate: punchPayment, isPending: isPunchPaymentLoading } =
    usePunchPayment();
  const { mutate: createLoanIntent, isPending: isCreateLoanIntentLoading } =
    useCreateLoanIntent();
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [tenureOptions, setTenureOptions] = useState([]);
  const [selectedTenure, setSelectedTenure] = useState(null);
  const [intendedLoanAmount, setIntendedLoanAmount] = useState("");
  const [loanIntentData, setLoanIntentData] = useState(null);
  const [loanApplicationNumber, setLoanApplicationNumber] = useState("");
  const [loanAmountErrorLogic, setLoanAmountErrorLogic] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState("");
  const [showPunchConfirmDialog, setShowPunchConfirmDialog] = useState(false);
  const [isLoanIntentCreated, setIsLoanIntentCreated] = useState(false);
  const [isLoanPunched, setIsLoanPunched] = useState(false);
  const [downPaymentLink, setDownPaymentLink] = useState("");
  const [forceEditLoanIntent, setForceEditLoanIntent] = useState(false);
  const hasPopulatedEditFromOrderRef = useRef(false);

  const orderLoanIntent = loanIntent ?? null;

  const showGreyishFromOrder =
    Boolean(orderLoanIntent && !loanIntentData) && !fromSwitchToLoan;
  const effectiveShowGreyish = showGreyishFromOrder && !forceEditLoanIntent;
  const loanIntentDisplayFromOrder = orderLoanIntent
    ? getLoanIntentDisplayFromOrder(orderLoanIntent, loanProviderDetails ?? [])
    : null;
  const showForm =
    (!showGreyishFromOrder || forceEditLoanIntent) && !isLoanIntentCreated;
  const showPostCreateFlow = !showGreyishFromOrder && isLoanIntentCreated;

  // Total collections = order amount paid (payments − refunds, from parent)
  const totalCollections = Number(orderAmountPaid) || 0;
  const intendedDownPayment =
    Number(
      loanIntentData?.down_payment ??
        orderLoanIntent?.down_payment ??
        (orderLoanIntent != null &&
        orderLoanIntent.remaining_down_payment_amount != null
          ? totalCollections +
            Number(orderLoanIntent.remaining_down_payment_amount)
          : 0),
    ) || 0;
  const rawRemainingDownPayment =
    intendedDownPayment - totalCollections;
  const hasOrderLoanIntentWithRemaining =
    orderLoanIntent != null &&
    orderLoanIntent.remaining_down_payment_amount != null;
  const remainingDownPayment =
    loanIntentData != null
      ? Math.max(0, rawRemainingDownPayment)
      : hasOrderLoanIntentWithRemaining
        ? Math.max(
            0,
            Number(orderLoanIntent.remaining_down_payment_amount) || 0,
          )
        : Math.max(0, rawRemainingDownPayment);
  const hasLoanIntent = loanIntentData != null || orderLoanIntent != null;
  const isDownPaymentFullyCovered = hasLoanIntent && remainingDownPayment <= 0;

  useEffect(() => {
    const hasContent =
      typeof dialogContent === "string"
        ? dialogContent.length > 0
        : Boolean(dialogContent);
    if (hasContent && !dialogOpen) window.location.reload();
  }, [dialogOpen, dialogContent]);

  useEffect(() => {
    const hasActiveLoanPayment = paymentDetails.some(
      (orderPayment) =>
        orderPayment?.method?.id === LOAN_PAYMENT_METHOD &&
        orderPayment?.status?.id !==
          PAYMENT_STATUSES.REJECTED_BY_INFINITY_LEARN,
    );
    setIsLoanPunched(hasActiveLoanPayment);
  }, [paymentDetails]);

  const hasLoanPaymentInCreatedOrDisbursed = paymentDetails.some(
    (orderPayment) => {
      if (orderPayment?.method?.id !== LOAN_PAYMENT_METHOD) return false;
      const paymentStatusId = orderPayment?.status?.id;
      const loanDetailsStatusId = orderPayment?.loan_details?.status?.id;
      return (
        paymentStatusId === PAYMENT_STATUSES.CREATED ||
        paymentStatusId === PAYMENT_STATUSES.DISBURSED ||
        loanDetailsStatusId === LOAN_STATUSES.CREATED ||
        loanDetailsStatusId === LOAN_STATUSES.DISBURSED
      );
    },
  );

  useEffect(() => {
    if (!isLoanIntentCreated && selectedProviderId && loanProviderDetails) {
      const list = Array.isArray(loanProviderDetails)
        ? loanProviderDetails
        : [];
      const provider = list.find(
        (loanProvider) => loanProvider.id === selectedProviderId,
      );
      if (provider && Array.isArray(provider.emi_divisions)) {
        setTenureOptions(provider.emi_divisions);
      }
    }
  }, [isLoanIntentCreated, selectedProviderId, loanProviderDetails]);

  useEffect(() => {
    if (!forceEditLoanIntent) {
      hasPopulatedEditFromOrderRef.current = false;
      return;
    }
    if (!orderLoanIntent || hasPopulatedEditFromOrderRef.current) return;
    const details = Array.isArray(loanProviderDetails) ? loanProviderDetails : [];
    const { providerId, tenureOption } = getProviderAndTenureFromOrderLoanIntent(
      orderLoanIntent,
      details,
    );
    const amount = orderLoanIntent.loan_application_amount;
    if (amount != null && amount !== "")
      setIntendedLoanAmount(String(Number(amount) || ""));
    if (providerId != null && details.length > 0) {
      setSelectedProviderId(providerId);
      const provider = details.find(
        (loanProvider) =>
          loanProvider.id === providerId ||
          String(loanProvider.id) === String(providerId),
      );
      if (provider && Array.isArray(provider.emi_divisions)) {
        setTenureOptions(provider.emi_divisions);
      }
    }
    if (tenureOption != null) setSelectedTenure(tenureOption);
    if (details.length > 0) hasPopulatedEditFromOrderRef.current = true;
  }, [forceEditLoanIntent, orderLoanIntent, loanProviderDetails]);

  useEffect(() => {
    if (isEmpty(selectedTenure) || !intendedLoanAmount) {
      setLoanAmountErrorLogic("");
      return;
    }

    const parsedIntendedLoanAmount = parseFloat(intendedLoanAmount);
    if (
      Number.isNaN(parsedIntendedLoanAmount) ||
      parsedIntendedLoanAmount <= 0
    ) {
      setLoanAmountErrorLogic("");
      return;
    }

    const minimumLoanAmountProviderAllows = !selectedTenure.minimum_loan_amount
      ? 0
      : selectedTenure.minimum_loan_amount;
    const maximumLoanAmountProviderAllows = !selectedTenure.maximum_loan_amount
      ? Number.MAX_SAFE_INTEGER
      : selectedTenure.maximum_loan_amount;

    const minDisplay = reduceToFixedDecimals(minimumLoanAmountProviderAllows);
    const maxDisplay = reduceToFixedDecimals(maximumLoanAmountProviderAllows);
    const enteredAmountDisplay = reduceToFixedDecimals(
      parsedIntendedLoanAmount,
    );
    const providerName =
      getSelectedProviderLabel(selectedProviderId, loanProviderDetails ?? []) ||
      "the provider";

    if (parsedIntendedLoanAmount > totalAmount) {
      setLoanAmountErrorLogic(
        `Entered amount (₹ ${enteredAmountDisplay}) is more than the order amount (₹ ${reduceToFixedDecimals(totalAmount)}). Please enter an amount up to the order amount.`,
      );
    } else if (parsedIntendedLoanAmount > maximumLoanAmountProviderAllows) {
      setLoanAmountErrorLogic(
        `Entered loan application amount (₹ ${enteredAmountDisplay}) for the provider (${providerName}) is not in the range of minimum loan amount (₹ ${minDisplay}) and maximum loan amount (₹ ${maxDisplay}).`,
      );
    } else if (parsedIntendedLoanAmount < minimumLoanAmountProviderAllows) {
      setLoanAmountErrorLogic(
        `Entered loan application amount (₹ ${enteredAmountDisplay}) for the provider (${providerName}) is not in the range of minimum loan amount (₹ ${minDisplay}) and maximum loan amount (₹ ${maxDisplay}).`,
      );
    } else {
      setLoanAmountErrorLogic("");
    }
  }, [
    intendedLoanAmount,
    selectedTenure,
    totalAmount,
    selectedProviderId,
    loanProviderDetails,
  ]);

  useEffect(() => {
    if (loanIntentData) {
      setIsLoanIntentCreated(true);
    }
  }, [loanIntentData]);

  const handleEditLoanIntentFromOrder = () => {
    if (!orderLoanIntent) return;
    const amount = orderLoanIntent.loan_application_amount;
    if (amount != null && amount !== "")
      setIntendedLoanAmount(String(Number(amount) || ""));
    const details = Array.isArray(loanProviderDetails) ? loanProviderDetails : [];
    const { providerId, tenureOption } = getProviderAndTenureFromOrderLoanIntent(
      orderLoanIntent,
      details,
    );
    if (providerId != null && details.length > 0) {
      setSelectedProviderId(providerId);
      const provider = details.find(
        (loanProvider) =>
          loanProvider.id === providerId ||
          String(loanProvider.id) === String(providerId),
      );
      if (provider && Array.isArray(provider.emi_divisions)) {
        setTenureOptions(provider.emi_divisions);
      }
    }
    if (tenureOption != null) setSelectedTenure(tenureOption);
    hasPopulatedEditFromOrderRef.current = true;
    setForceEditLoanIntent(true);
  };

  if (isLoanProviderDetailLoading) {
    return <Loader />;
  }

  if (isLoanPunched) {
    return (
      <div className={styles.loanPaymentContainer}>
        {fromSwitchToLoan ? (
          <Text className={styles.loanAlreadyExistsInfo}>
            A loan is already in place for this order. You can collect any
            remaining amount in the section below.
          </Text>
        ) : (
          <ErrorMessage>
            A loan payment already exists for this order. Please use the edit
            flow to modify it.
          </ErrorMessage>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={styles.loanPaymentContainer}>
        {effectiveShowGreyish ? (
          <>
            <div className={styles.field}>
              <Text>Select Loan Provider</Text>
              <div className={styles.laaAmountDisplayGrey}>
                {loanIntentDisplayFromOrder?.providerName ?? "—"}
              </div>
            </div>
            <div className={styles.field}>
              <Text>Select Tenure</Text>
              <div className={styles.laaAmountDisplayGrey}>
                {loanIntentDisplayFromOrder?.tenureMonths ?? "—"}
              </div>
            </div>
            <div className={styles.laaSection}>
              <Text>Loan Application Amount</Text>
              <div className={styles.laaInputRow}>
                <div className={styles.laaInputWrap}>
                  <div className={styles.laaAmountDisplayGrey}>
                    {loanIntentDisplayFromOrder?.loanApplicationAmount ?? "—"}
                  </div>
                </div>
                {!hasLoanPaymentInCreatedOrDisbursed && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<EditIcon />}
                    onClick={handleEditLoanIntentFromOrder}
                    title="Change details and click Create Loan Intent again"
                  >
                    Edit Loan Intent
                  </Button>
                )}
              </div>
            </div>
            {orderLoanIntent && (() => {
              const resolvedTenure = getTenureFromOrderLoanIntent(
                orderLoanIntent,
                loanProviderDetails ?? [],
              );
              const loanAmount =
                Number(orderLoanIntent?.loan_application_amount) || 0;
              const downPaymentPct =
                resolvedTenure?.down_payment_percentage ?? 0;
              const downPaymentAmt = getDownPaymentForPunch(
                orderLoanIntent,
                intendedDownPayment,
                resolvedTenure,
                loanAmount,
              );
              const tenureMonths =
                Number(resolvedTenure?.tenure_in_months) || 0;
              const monthlyEmi =
                tenureMonths > 0 && loanAmount - downPaymentAmt > 0
                  ? (loanAmount - downPaymentAmt) / tenureMonths
                  : 0;
              return (
                <div className={styles.loanDetails}>
                  <Detail
                    title="Loan Amount"
                    value={reduceToFixedDecimals(loanAmount)}
                  />
                  <Detail
                    title="Downpayment Percentage"
                    value={`${downPaymentPct}`}
                  />
                  <Detail
                    title="Downpayment Amount"
                    value={reduceToFixedDecimals(downPaymentAmt)}
                  />
                  <Detail
                    title="Monthly EMI"
                    value={reduceToFixedDecimals(monthlyEmi)}
                  />
                </div>
              );
            })()}
            {hasLoanIntent && (
              <>
                {!isDownPaymentFullyCovered ? (
                  <div className={styles.downPaymentLinkBox}>
                    <div className={styles.calculatedDpAmountBox}>
                      <Text>Calculated down-payment</Text>
                      <div className={styles.calculatedDpAmountValue}>
                        {orderLoanIntent?.down_payment != null
                          ? Number(orderLoanIntent.down_payment)
                          : remainingDownPayment > 0
                            ? totalCollections + remainingDownPayment
                            : "—"}
                      </div>
                    </div>
                    {rawRemainingDownPayment > 0 && (
                      <div className={styles.downPaymentMessageWrap}>
                        <ErrorMessage>
                          Please collect ₹{" "}
                          {reduceToFixedDecimals(rawRemainingDownPayment)} more
                          to punch this loan.
                        </ErrorMessage>
                      </div>
                    )}
                    {rawRemainingDownPayment < 0 && (
                      <div
                        className={`${styles.downPaymentMessageWrap} ${styles.downPaymentInfoMessage}`}
                      >
                        Collections ₹{" "}
                        {reduceToFixedDecimals(totalCollections)} exceed the
                        intended loan&apos;s down payment (₹{" "}
                        {reduceToFixedDecimals(intendedDownPayment)}) by ₹{" "}
                        {reduceToFixedDecimals(
                          totalCollections - intendedDownPayment,
                        )}
                        . The excess will be adjusted against the remaining
                        order balance. Loan punching is allowed!
                      </div>
                    )}
                    <div className={styles.paymentLinkColumn}>
                      <PaymentModes
                        orderId={selectedOrderId}
                        amountToBePaid={reduceToFixedDecimals(
                          remainingDownPayment,
                        )}
                        createdBy={salesPersonId}
                        paymentLink={downPaymentLink}
                        setPaymentLink={setDownPaymentLink}
                        paymentTypeId={2}
                        paymentFlowTypeId={PAYMENT_PLANS.LOAN}
                        firstPaymentCollectionAmount={0}
                        selectedOrderVerticalId={selectedOrderVerticalId}
                        submitButtonLabel="Create Down Payment Link"
                      />
                    </div>
                  </div>
                ) : null}
                {isDownPaymentFullyCovered
                  ? (() => {
                      const resolvedTenure = getTenureFromOrderLoanIntent(
                        orderLoanIntent,
                        loanProviderDetails ?? [],
                      );
                      const orderIntentLoanAppAmount =
                        Number(orderLoanIntent?.loan_application_amount) || 0;
                      const orderIntentDownPayment = getDownPaymentForPunch(
                        orderLoanIntent,
                        intendedDownPayment,
                        resolvedTenure,
                        orderIntentLoanAppAmount,
                      );
                      const punchAmount = getPunchLoanAmount(
                        orderIntentLoanAppAmount,
                        orderIntentDownPayment,
                      );
                      const syntheticLoanIntentData = {
                        loan_amount: punchAmount,
                        loan_application_amount: orderIntentLoanAppAmount,
                        down_payment: orderIntentDownPayment,
                      };
                      const totalSuccessfulPayments = reduceToFixedDecimals(
                        paymentDetails
                          .filter((payment) =>
                            SUCCESSFUL_PAYMENT_STATUSES.includes(
                              payment?.status?.id,
                            ),
                          )
                          .reduce(
                            (sum, payment) => sum + payment.amount,
                            0,
                          ),
                      );
                      const intendedDp =
                        orderLoanIntent?.down_payment ?? totalCollections;
                      const validationAmount =
                        punchAmount +
                        (totalSuccessfulPayments - intendedDp);
                      const isValid = validationAmount <= totalAmount;
                      return (
                        <>
                          {rawRemainingDownPayment < 0 && (
                            <div
                              className={`${styles.downPaymentMessageWrap} ${styles.downPaymentInfoMessage}`}
                            >
                              Collections ₹{" "}
                              {reduceToFixedDecimals(totalCollections)} exceed
                              the intended loan&apos;s down payment (₹{" "}
                              {reduceToFixedDecimals(intendedDownPayment)}) by
                              ₹{" "}
                              {reduceToFixedDecimals(
                                totalCollections - intendedDownPayment,
                              )}
                              . The excess will be adjusted against the
                              remaining order balance. Loan punching is allowed!
                            </div>
                          )}
                          {hasLoanPaymentInCreatedOrDisbursed && (
                            <ErrorMessage>
                              A loan payment already exists in Created or
                              Disbursed state. Cannot punch another loan.
                            </ErrorMessage>
                          )}
                          {!hasLoanPaymentInCreatedOrDisbursed && (
                            <>
                              <div
                                className={`${styles.field} ${styles.loanApplicationNumberInput}`}
                              >
                                <Text>Loan Application Number</Text>
                                <Textfield
                                  label="Enter Application Number"
                                  value={loanApplicationNumber}
                                  handleOnChange={(event) =>
                                    setLoanApplicationNumber(event.target.value)
                                  }
                                />
                                {loanApplicationNumber.trim() === "" && (
                                  <ErrorMessage>
                                    Loan Application Number is required.
                                  </ErrorMessage>
                                )}
                              </div>
                              <PrimaryButton
                                onClick={() => {
                                  if (fromSwitchToLoan) {
                                    setShowPunchConfirmDialog(true);
                                  } else {
                                  handlePunchPayment(
                                    selectedOrderId,
                                    punchAmount,
                                    loanApplicationNumber,
                                    syntheticLoanIntentData,
                                    resolvedTenure,
                                    fromSwitchToLoan,
                                    punchPayment,
                                    setDialogContent,
                                    setDialogOpen,
                                    openSnackbar,
                                  );
                                }
                              }}
                              disabled={
                                hasLoanPaymentInCreatedOrDisbursed ||
                                !resolvedTenure ||
                                !isFormValidForPunchLoan(
                                  loanApplicationNumber,
                                ) ||
                                !isValid ||
                                isPunchPaymentLoading ||
                                punchAmount <= 0
                              }
                              >
                                {isPunchPaymentLoading ? (
                                  <Loader />
                                ) : (
                                  "Punch loan"
                                )}
                              </PrimaryButton>
                            </>
                          )}
                        </>
                      );
                    })()
                  : null}
              </>
            )}
          </>
        ) : showForm ? (
          <>
            <div className={styles.field}>
              <Text>Select Loan Provider</Text>
              <SingleSelect
                tag="Loan Provider"
                menuItems={getLoanProvider(loanProviderDetails)}
                handleChange={(event) =>
                  handleProviderChange(
                    event,
                    setSelectedProviderId,
                    setTenureOptions,
                    setSelectedTenure,
                    loanProviderDetails,
                    setLoanApplicationNumber,
                    setLoanAmountErrorLogic,
                    setLoanIntentData,
                    setIsLoanIntentCreated,
                  )
                }
                value={selectedProviderId}
              />
            </div>
            <div className={styles.field}>
              <Text>Select Tenure</Text>
              <SingleSelect
                tag="Tenure"
                defaultSelected={"Select"}
                menuItems={getTenureOptions(tenureOptions)}
                handleChange={(event) =>
                  handleTenureChange(
                    event,
                    setSelectedTenure,
                    setLoanApplicationNumber,
                    setLoanAmountErrorLogic,
                    setLoanIntentData,
                    setIsLoanIntentCreated,
                  )
                }
                value={selectedTenure}
                disabled={!selectedProviderId}
              />
            </div>
            {selectedTenure && (
              <div className={styles.laaSection}>
                <Text>Loan Application Amount</Text>
                <div className={styles.laaInputRow}>
                  <div className={styles.laaInputWrap}>
                    <Textfield
                      value={intendedLoanAmount}
                      handleOnChange={(event) => {
                        const value = event.target.value;
                        const validAmount = value
                          .replace(/[^0-9.]/g, "")
                          .replace(/(\..*?)\..*/g, "$1")
                          .replace(/(\.\d{2})\d*/g, "$1");
                        setIntendedLoanAmount(validAmount);
                      }}
                      label="Enter Loan Application Amount"
                    />
                  </div>
                  <PrimaryButton
                    onClick={() =>
                      handleCreateLoanIntent(
                        selectedOrderId,
                        parseFloat(intendedLoanAmount),
                        selectedTenure,
                        createLoanIntent,
                        setLoanIntentData,
                        openSnackbar,
                      )
                    }
                    disabled={
                      !isFormValidForLoanIntent(
                        selectedProviderId,
                        selectedTenure,
                        parseFloat(intendedLoanAmount),
                      ) ||
                      isCreateLoanIntentLoading ||
                      !!loanAmountErrorLogic
                    }
                  >
                    {isCreateLoanIntentLoading ? (
                      <Loader />
                    ) : (
                      "Create Loan Intent"
                    )}
                  </PrimaryButton>
                </div>
              </div>
            )}
            {loanAmountErrorLogic && (
              <ErrorMessage>{loanAmountErrorLogic}</ErrorMessage>
            )}
          </>
        ) : (
          <>
            <div className={styles.field}>
              <Text>Select Loan Provider</Text>
              <div className={styles.laaAmountDisplayMuted}>
                {getSelectedProviderLabel(
                  selectedProviderId,
                  loanProviderDetails,
                )}
              </div>
            </div>
            <div className={styles.field}>
              <Text>Select Tenure</Text>
              <div className={styles.laaAmountDisplayMuted}>
                {selectedTenure?.tenure_in_months ?? "—"}
              </div>
            </div>
            <div className={styles.laaSection}>
              <Text>Loan Application Amount</Text>
              <div className={styles.laaInputRow}>
                <div className={styles.laaInputWrap}>
                  <div className={styles.laaAmountDisplayMuted}>
                    {Number(intendedLoanAmount || 0)}
                  </div>
                </div>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    setIsLoanIntentCreated(false);
                    setLoanIntentData(null);
                    setDownPaymentLink("");
                    setLoanApplicationNumber("");
                  }}
                  title="Change details and click Create Loan Intent again"
                >
                  Edit Loan Intent
                </Button>
              </div>
            </div>
            {loanAmountErrorLogic && (
              <ErrorMessage>{loanAmountErrorLogic}</ErrorMessage>
            )}
          </>
        )}
        {showPostCreateFlow && (
          <>
            <div className={styles.loanDetails}>
              <Detail
                title="Loan Amount"
                value={reduceToFixedDecimals(loanIntentData?.loan_amount || 0)}
              />
              <Detail
                title="Downpayment Percentage"
                value={`${loanIntentData?.down_payment_percentage ?? 0}`}
              />
              <Detail
                title="Downpayment Amount"
                value={reduceToFixedDecimals(loanIntentData?.down_payment || 0)}
              />
              <Detail
                title="Monthly EMI"
                value={reduceToFixedDecimals(loanIntentData?.monthly_emi || 0)}
              />
            </div>

            {!isDownPaymentFullyCovered ? (
              <div className={styles.downPaymentLinkBox}>
                <div className={styles.calculatedDpAmountBox}>
                  <Text>Calculated down-payment</Text>
                  <div className={styles.calculatedDpAmountValue}>
                    {Number(loanIntentData?.down_payment ?? 0)}
                  </div>
                </div>
                {(() => {
                  const raw = rawRemainingDownPayment;
                  if (raw > 0) {
                    return (
                      <div className={styles.downPaymentMessageWrap}>
                        <ErrorMessage>
                          Please collect ₹ {reduceToFixedDecimals(raw)} more to
                          punch this loan.
                        </ErrorMessage>
                      </div>
                    );
                  }
                  if (raw < 0) {
                    const excessAmount = totalCollections - intendedDownPayment;
                    return (
                      <div
                        className={`${styles.downPaymentMessageWrap} ${styles.downPaymentInfoMessage}`}
                      >
                        Collections ₹{" "}
                        {reduceToFixedDecimals(totalCollections)} exceed the
                        intended loan&apos;s down payment (₹{" "}
                        {reduceToFixedDecimals(intendedDownPayment)}) by ₹{" "}
                        {reduceToFixedDecimals(excessAmount)}. The excess will
                        be adjusted against the remaining order balance. Loan
                        punching is allowed!
                      </div>
                    );
                  }
                  return null;
                })()}
                <div className={styles.paymentLinkColumn}>
                  <PaymentModes
                    orderId={selectedOrderId}
                    amountToBePaid={reduceToFixedDecimals(remainingDownPayment)}
                    createdBy={salesPersonId}
                    paymentLink={downPaymentLink}
                    setPaymentLink={setDownPaymentLink}
                    paymentTypeId={2}
                    paymentFlowTypeId={PAYMENT_PLANS.LOAN}
                    firstPaymentCollectionAmount={0}
                    selectedOrderVerticalId={selectedOrderVerticalId}
                    submitButtonLabel="Create Down Payment Link"
                  />
                </div>
              </div>
            ) : null}

            {!isDownPaymentFullyCovered ? null : (
              <>
                {(() => {
                  const totalSuccessfulPayments = reduceToFixedDecimals(
                    paymentDetails
                      .filter((orderPayment) =>
                        SUCCESSFUL_PAYMENT_STATUSES.includes(
                          orderPayment?.status?.id,
                        ),
                      )
                      .reduce(
                        (totalPaid, orderPayment) =>
                          totalPaid + orderPayment.amount,
                        0,
                      ),
                  );
                  const validationAmount =
                    loanIntentData?.loan_amount +
                    (totalSuccessfulPayments - loanIntentData?.down_payment);
                  const isValid = validationAmount <= totalAmount;

                  return (
                    <>
                      {rawRemainingDownPayment < 0 && (
                        <div
                          className={`${styles.downPaymentMessageWrap} ${styles.downPaymentInfoMessage}`}
                        >
                          Collections ₹{" "}
                          {reduceToFixedDecimals(totalCollections)} exceed the
                          intended loan&apos;s down payment (₹{" "}
                          {reduceToFixedDecimals(intendedDownPayment)}) by ₹{" "}
                          {reduceToFixedDecimals(
                            totalCollections - intendedDownPayment,
                          )}
                          . The excess will be adjusted against the remaining
                          order balance. Loan punching is allowed!
                        </div>
                      )}
                      {hasLoanPaymentInCreatedOrDisbursed && (
                        <ErrorMessage>
                          A loan payment already exists in Created or Disbursed
                          state. Cannot punch another loan.
                        </ErrorMessage>
                      )}
                      {!isValid && !hasLoanPaymentInCreatedOrDisbursed && (
                        <ErrorMessage>
                          The intended loan amount and collected payments exceed
                          the order amount.
                        </ErrorMessage>
                      )}
                      <div
                        className={`${styles.field} ${styles.loanApplicationNumberInput}`}
                      >
                        <Text>Loan Application Number</Text>
                        <Textfield
                          label="Enter Application Number"
                          value={loanApplicationNumber}
                          handleOnChange={(event) =>
                            setLoanApplicationNumber(event.target.value)
                          }
                        />
                        {loanApplicationNumber.trim() === "" && (
                          <ErrorMessage>
                            Loan Application Number is required.
                          </ErrorMessage>
                        )}
                      </div>
                      <PrimaryButton
                        onClick={() => {
                          if (fromSwitchToLoan) {
                            setShowPunchConfirmDialog(true);
                          } else {
                            handlePunchPayment(
                              selectedOrderId,
                              loanIntentData?.loan_amount,
                              loanApplicationNumber,
                              loanIntentData,
                              selectedTenure,
                              fromSwitchToLoan,
                              punchPayment,
                              setDialogContent,
                              setDialogOpen,
                              openSnackbar,
                            );
                          }
                        }}
                        disabled={
                          hasLoanPaymentInCreatedOrDisbursed ||
                          !isFormValidForPunchLoan(loanApplicationNumber) ||
                          !isValid ||
                          isPunchPaymentLoading ||
                          !loanIntentData?.loan_amount ||
                          loanIntentData.loan_amount <= 0
                        }
                      >
                        {isPunchPaymentLoading ? <Loader /> : "Punch loan"}
                      </PrimaryButton>
                    </>
                  );
                })()}
              </>
            )}
          </>
        )}
      </div>
      <Dialog
        open={dialogOpen}
        handleClose={() => handleDialogClose(setDialogOpen)}
        title={
          <span
            style={{
              display: "block",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            Loan Status
          </span>
        }
        height="auto"
      >
        <div style={{ textAlign: "center" }}>
          {typeof dialogContent === "string" ? (
            <span style={{ whiteSpace: "pre-line" }}>{dialogContent}</span>
          ) : (
            dialogContent
          )}
        </div>
      </Dialog>
      <Dialog
        open={showPunchConfirmDialog}
        handleClose={() => setShowPunchConfirmDialog(false)}
        title="Switch to Loan"
        height="auto"
      >
        <div className={styles.punchConfirmContent}>
          <p className={styles.punchConfirmMessage}>
            Are you sure you want to change the payment type? This action cannot
            be reverted.
          </p>
          <div className={styles.punchConfirmActions}>
            <ErrorButton onClick={() => setShowPunchConfirmDialog(false)}>
              Cancel and Go Back
            </ErrorButton>
            <PrimaryButton
              onClick={() => {
                setShowPunchConfirmDialog(false);
                const fromOrder =
                  showGreyishFromOrder && orderLoanIntent && !loanIntentData;
                const punchTenureForConfirm = fromOrder
                  ? getTenureFromOrderLoanIntent(
                      orderLoanIntent,
                      loanProviderDetails ?? [],
                    )
                  : null;
                const orderAppAmount = Number(
                  orderLoanIntent?.loan_application_amount ?? 0,
                );
                const downPaymentForConfirm = fromOrder
                  ? getDownPaymentForPunch(
                      orderLoanIntent,
                      intendedDownPayment,
                      punchTenureForConfirm,
                      orderAppAmount,
                    )
                  : 0;
                const punchLoanAmount = fromOrder
                  ? getPunchLoanAmount(orderAppAmount, downPaymentForConfirm)
                  : loanIntentData?.loan_amount;
                const punchIntentData = fromOrder
                  ? {
                      loan_amount: punchLoanAmount,
                      loan_application_amount:
                        orderLoanIntent?.loan_application_amount,
                      down_payment: downPaymentForConfirm,
                    }
                  : loanIntentData;
                const punchTenure = fromOrder
                  ? punchTenureForConfirm
                  : selectedTenure;
                if (fromOrder && !punchTenure) return;
                handlePunchPayment(
                  selectedOrderId,
                  punchLoanAmount,
                  loanApplicationNumber,
                  punchIntentData,
                  punchTenure,
                  fromSwitchToLoan,
                  punchPayment,
                  setDialogContent,
                  setDialogOpen,
                  openSnackbar,
                );
              }}
            >
              Confirm and Proceed
            </PrimaryButton>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default LoanPayment;
